import { on } from 'delegated-events';
import { createFocusTrap } from 'focus-trap';
import elementIndex from '../../../javascripts/utils/elementIndex';
import abort from '../../../javascripts/utils/abort';
import moveFocus from '../../../javascripts/utils/moveFocus';

const fitImageInsideContainer = ($image: HTMLElement, $container: HTMLElement) => {
  const { width: containerWidth, height: containerHeight } = $container.getBoundingClientRect();
  const $img = $image instanceof HTMLImageElement ? $image : $image.querySelector('img');
  const { width, height } = $img ?? {};

  if (width && height) {
    const imageRatio = width / height;
    const fittedRatio = containerHeight / (containerWidth / imageRatio);
    const maxWidth = Math.min(1, fittedRatio) * 100;

    $image.style.maxWidth = `${maxWidth}%`;
  }
};

on('click', '[data-gallery]', (triggerEvent) => {
  const { currentTarget: $trigger } = triggerEvent;
  const { gallery: templateId } = $trigger.dataset;

  if (!templateId) {
    return;
  }

  const $gallery = $trigger.closest<HTMLElement>('.gallery') ?? abort();
  const $template = document.querySelector<HTMLTemplateElement>(`#${templateId}`) ?? abort();
  const $fragment = $template.content.cloneNode(true) as DocumentFragment;
  const $overlay = $fragment.querySelector<HTMLElement>('.gallery__overlay') ?? abort();
  const $container = $overlay.querySelector<HTMLElement>('.gallery__overlay-container') ?? abort();
  const $position = $overlay.querySelector('.gallery__position') ?? abort();

  const currentImage = () => $overlay.querySelector<HTMLElement>('.gallery__image:not([hidden])') ?? abort();
  const moveImage = (by: number) => {
    const $$image = $overlay.querySelectorAll<HTMLElement>('.gallery__image');
    const $currentImage = currentImage();
    const currentPosition = elementIndex($currentImage) + 1;

    let newPosition = currentPosition + by;

    if (newPosition > $$image.length) {
      newPosition %= $$image.length;
    }

    if (newPosition <= 0) {
      newPosition = $$image.length + newPosition;
    }

    const $nextImage = $$image[newPosition - 1] ?? abort();
    $nextImage.dataset.moveDirection = by > 0 ? 'left' : 'right';
    $currentImage.toggleAttribute('hidden', true);
    $nextImage.toggleAttribute('hidden', false);
    $position.textContent = `${newPosition}/${$$image.length}`;

    fitImageInsideContainer($nextImage, $container);
    moveFocus($nextImage);
  };

  const resizeOberserver = new ResizeObserver(() => {
    fitImageInsideContainer(currentImage(), $container);
  });

  const focusTrap = createFocusTrap($overlay, {
    clickOutsideDeactivates: false,
    escapeDeactivates: true,
    onActivate() {
      fitImageInsideContainer(currentImage(), $container);
      resizeOberserver.observe($overlay);
    },
    onDeactivate() {
      resizeOberserver.disconnect();
    },
    onPostDeactivate() {
      $overlay.remove();
    },
  });

  $overlay.addEventListener('keydown', (event) => {
    if (event.code === 'ArrowRight') {
      event.preventDefault();
      moveImage(1);
    } else if (event.code === 'ArrowLeft') {
      event.preventDefault();
      moveImage(-1);
    }
  });

  $overlay.addEventListener('click', (event) => {
    const { target: $target } = event;

    if ($target instanceof Element) {
      const { galleryAction: action } = $target?.closest<HTMLElement>('[data-gallery-action]')?.dataset ?? {};

      if (action === 'prev' || action === 'next') {
        event.preventDefault();
        moveImage(action === 'prev' ? -1 : 1);
      }

      if (action === 'close') {
        event.preventDefault();
        focusTrap.deactivate();
      }
    }
  });

  $gallery.appendChild($overlay);
  focusTrap.activate();
});
